import { useCallback, useEffect, useState } from 'react';
import { api, type Message } from '../../api/client';
import { Carte, Chargement, EtatVide, MessageErreur, Puce } from '../../components/Ui';
import {
  etatFacturation,
  formaterDate,
  formaterDateLongue,
  formaterFcfa,
  formaterNumero,
  libelleEncodage,
  mentionFacturation,
  operateurDe,
} from '../../lib/format';

const FILTRES: { valeur: string; libelle: string }[] = [
  { valeur: '', libelle: 'Tous les états' },
  { valeur: 'queued,submitted,sent', libelle: 'En cours' },
  { valeur: 'delivered', libelle: 'Délivrés' },
  { valeur: 'undelivered,expired,rejected', libelle: 'En échec' },
  { valeur: 'opted_out', libelle: 'Filtrés (STOP)' },
];

export function Journal({
  surVoirMessage,
  technique = false,
}: {
  surVoirMessage: (id: string) => void;
  /**
   * Vue technique, pour l'espace développeur.
   *
   * Les deux publics ne cherchent pas la même chose dans ce tableau. Le métier
   * demande « mon message est-il arrivé, et combien m'a-t-il coûté » ; celui qui
   * intègre demande « pourquoi celui-ci a échoué, et à quoi le rattacher dans
   * mon système ». D'où l'identifiant public, la référence client et le code
   * d'erreur d'un côté, l'opérateur et le coût de l'autre.
   */
  technique?: boolean;
}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [curseur, setCurseur] = useState<string | null>(null);
  const [encore, setEncore] = useState(false);
  const [statut, setStatut] = useState('');
  const [destinataire, setDestinataire] = useState('');
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<unknown>(null);

  const charger = useCallback(
    async (depuis: string | null, remplacer: boolean) => {
      setChargement(true);
      setErreur(null);
      try {
        const page = await api.journal({
          status: statut || undefined,
          to: destinataire || undefined,
          cursor: depuis,
          limit: 25,
        });
        setMessages((actuels) => (remplacer ? page.data : [...actuels, ...page.data]));
        setCurseur(page.next_cursor);
        setEncore(page.has_more);
      } catch (e) {
        setErreur(e);
      } finally {
        setChargement(false);
      }
    },
    [statut, destinataire],
  );

  // Tout changement de filtre repart de zéro : un curseur ne survit pas au
  // changement du jeu de résultats qu'il parcourt.
  useEffect(() => {
    void charger(null, true);
  }, [charger]);

  return (
    <>
      <MessageErreur erreur={erreur} />

      <Carte sansCorps>
        <div className="corps" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ flex: '0 0 220px' }}>
            <select value={statut} onChange={(e) => setStatut(e.target.value)}>
              {FILTRES.map((f) => (
                <option key={f.valeur} value={f.valeur}>
                  {f.libelle}
                </option>
              ))}
            </select>
          </label>
          <label style={{ flex: '1 1 240px' }}>
            <input
              type="text"
              value={destinataire}
              onChange={(e) => setDestinataire(e.target.value)}
              placeholder="Filtrer par destinataire — +24166001242"
            />
          </label>
        </div>

        {messages.length === 0 && !chargement ? (
          <EtatVide titre="Aucun message ne correspond à ces filtres.">
            <button
              className="secondaire"
              onClick={() => {
                setStatut('');
                setDestinataire('');
              }}
            >
              Réinitialiser les filtres
            </button>
          </EtatVide>
        ) : (
          <div className="tableau-conteneur">
            <table style={{ minWidth: 760 }}>
              <thead>
                <tr>
                  <th>Date</th>
                  {technique && <th>Identifiant</th>}
                  <th>Destinataire</th>
                  <th>Expéditeur</th>
                  {technique ? <th>Référence client</th> : <th>Opérateur</th>}
                  <th className="droite">Segments</th>
                  {technique ? <th>Erreur</th> : <th className="droite">Coût</th>}
                  <th>Alphabet</th>
                  <th>État</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((m) => (
                  <tr key={m.id}>
                    <td className="mono" style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                      {formaterDate(m.created_at)}
                    </td>
                    {technique && (
                      <td className="mono" style={{ fontSize: 11.5, color: 'var(--muet)' }}>
                        <button className="lien" onClick={() => surVoirMessage(m.id)}>
                          {m.id.slice(0, 12)}…
                        </button>
                      </td>
                    )}
                    <td className="mono" style={{ fontSize: 12.5 }}>
                      <button className="lien" onClick={() => surVoirMessage(m.id)}>
                        {formaterNumero(m.to)}
                      </button>
                    </td>
                    <td>
                      <span className="etiquette-sid">{m.from}</span>
                    </td>
                    {technique ? (
                      <td className="mono" style={{ fontSize: 12, color: 'var(--muet)' }}>
                        {m.client_reference ?? '—'}
                      </td>
                    ) : (
                      <td style={{ color: 'var(--muet)', fontSize: 13 }}>{operateurDe(m.to)}</td>
                    )}
                    <td className="nombre">{m.segments}</td>
                    {technique ? (
                      <td className="mono" style={{ fontSize: 12 }}>
                        {m.error_code ? (
                          <span style={{ color: 'var(--rouge)' }} title={m.error_message ?? ''}>
                            {m.error_code}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--muet)' }}>—</span>
                        )}
                      </td>
                    ) : (
                      <td className="nombre">{formaterFcfa(m.cost_xaf)}</td>
                    )}
                    <td style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                      {libelleEncodage(m.encoding)}
                    </td>
                    <td>
                      <Puce valeur={m.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {chargement && <Chargement quoi="Chargement du journal…" />}

        {encore && !chargement && (
          <div style={{ padding: 16, textAlign: 'center' }}>
            <button className="secondaire" onClick={() => void charger(curseur, false)}>
              Charger la suite
            </button>
          </div>
        )}
      </Carte>
    </>
  );
}

/** Statuts pour lesquels on continue d'interroger l'API. */
const EN_MOUVEMENT = new Set(['queued', 'submitted', 'sent']);

export function DetailMessage({ id, surRetour }: { id: string; surRetour: () => void }) {
  const [message, setMessage] = useState<Message | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);

  useEffect(() => {
    let actif = true;

    const charger = () =>
      api
        .message(id)
        .then((donnees) => {
          if (actif) setMessage(donnees);
          return donnees;
        })
        .catch((e) => {
          if (actif) setErreur(e);
          return null;
        });

    void charger();

    /**
     * On interrogera vite au début, puis de plus en plus lentement.
     *
     * Un message se fige d'ordinaire en quelques secondes — l'accusé de Moov
     * arrive en moins d'une — et l'interrogation s'arrête alors d'elle-même.
     * Mais un message dont l'accusé ne vient jamais, faute que l'opérateur en
     * émette, restait interrogé toutes les trois secondes tant que l'onglet
     * était ouvert : des milliers de requêtes pour un état qui ne bouge pas,
     * payées en données mobiles et en batterie.
     *
     * Le délai croît donc de moitié à chaque tour, jusqu'à une minute. Les
     * premières secondes restent aussi réactives qu'avant, et une attente
     * longue ne coûte plus qu'un appel par minute.
     */
    const DELAI_INITIAL_MS = 3_000;
    const DELAI_MAXIMAL_MS = 60_000;
    let delai = DELAI_INITIAL_MS;
    let minuterie: ReturnType<typeof setTimeout>;

    const planifier = () => {
      minuterie = setTimeout(async () => {
        const donnees = await charger();
        if (!actif) return;
        // Figé : il n'y a plus rien à attendre.
        if (donnees && !EN_MOUVEMENT.has(donnees.status)) return;
        delai = Math.min(Math.round(delai * 1.5), DELAI_MAXIMAL_MS);
        planifier();
      }, delai);
    };
    planifier();

    return () => {
      actif = false;
      clearTimeout(minuterie);
    };
  }, [id]);

  if (erreur) return <MessageErreur erreur={erreur} />;
  if (!message) return <Chargement quoi="Chargement du message…" />;

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <button className="lien" onClick={surRetour}>
          ← Retour au journal
        </button>
      </div>

      <div className="rangee">
        <div className="large">
          <Carte titre="Contenu">
            <dl className="definitions">
              <dt>Identifiant</dt>
              <dd className="mono" style={{ userSelect: 'all', fontSize: 12.5 }}>
                {message.id}
              </dd>

              <dt>Destinataire</dt>
              <dd className="mono">{formaterNumero(message.to)}</dd>

              <dt>Opérateur</dt>
              <dd>{operateurDe(message.to)}</dd>

              <dt>Expéditeur</dt>
              <dd>{message.from}</dd>

              <dt>Nature</dt>
              <dd>{message.category === 'marketing' ? 'Publicitaire' : 'Transactionnel'}</dd>

              <dt>Environnement</dt>
              <dd>{message.environment === 'test' ? 'Bac à sable' : 'Production'}</dd>

              <dt>Alphabet</dt>
              <dd>{libelleEncodage(message.encoding)}</dd>

              <dt>Segments</dt>
              <dd>{message.segments}</dd>

              <dt>Prix du segment</dt>
              <dd>{formaterFcfa(message.price_per_segment_xaf)}</dd>

              <dt>Coût total</dt>
              <dd>
                {(() => {
                  // Le montant du message est un coût calculé, pas une écriture
                  // au grand livre : il faut dire s'il a été prélevé.
                  const etat = etatFacturation(message.status, message.environment);
                  const mention = mentionFacturation(etat);
                  return (
                    <>
                      <strong
                        style={
                          etat === 'facture' ? undefined : { textDecoration: 'line-through' }
                        }
                      >
                        {formaterFcfa(message.cost_xaf)}
                      </strong>
                      {mention && (
                        <span style={{ marginLeft: 8, color: 'var(--muet)' }}>
                          {mention}
                        </span>
                      )}
                    </>
                  );
                })()}
              </dd>

              {message.error_code && (
                <>
                  <dt>Code d’erreur</dt>
                  <dd style={{ color: 'var(--rouge)' }}>{message.error_code}</dd>
                </>
              )}
            </dl>

            {message.body && (
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  background: 'var(--fond)',
                  border: '1px solid var(--bordure)',
                  borderRadius: 8,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {message.body}
              </div>
            )}
          </Carte>
        </div>

        <div className="etroit">
          <Carte titre="Cycle de vie">
            <ol className="chronologie">
              {(message.events ?? []).map((evenement, index) => (
                <li key={`${evenement.status}-${index}`}>
                  <time dateTime={evenement.occurred_at} style={{ width: 'auto' }}>
                    {formaterDateLongue(evenement.occurred_at)}
                  </time>
                  <Puce valeur={evenement.status} />
                  {evenement.detail && (
                    <span style={{ color: 'var(--muet)', flexBasis: '100%' }}>
                      {evenement.detail}
                    </span>
                  )}
                </li>
              ))}
            </ol>

            {EN_MOUVEMENT.has(message.status) && (
              <div className="aide">
                Ce message n’a pas encore atteint son état final. La page se met à jour
                automatiquement.
              </div>
            )}
          </Carte>
        </div>
      </div>
    </>
  );
}
